import {gql} from '@apollo/client';
import {getTestProps} from '@benchling/js-test-utils';
import {
  Anchor,
  Badge,
  Box,
  Button,
  List,
  createStyles,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import React, {useCallback, useMemo, useState} from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TeamRole} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import staticURL from 'lib/staticURL';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {MenuHeight, MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {CenteredSpinner} from 'ui-library/Spinner';
import APIUtil from 'util/APIUtil';
import {typeToName} from 'util/NameUtil';
import sortedBy from 'util/sortedBy';
import {assertNever} from 'util/Types';

import {ReadableTeamsQuery, ReadableTeamsQueryVariables} from './__generated/ReadableTeamsQuery';

const EMPTY_TEAMS_ILLUSTRATION_PATH = '/static/img/llm/empty-teams-jellyfish.png';

const useEmptyTeamsIllustrationStyles = createStyles((theme) => ({
  illustration: {
    width: 110,
    height: 'auto',
    marginBottom: theme.spacing.md,
    userSelect: 'none',
  },
}));

function EmptyTeamsIllustration(): JSX.Element {
  const {classes} = useEmptyTeamsIllustrationStyles();
  return (
    <img
      src={staticURL(EMPTY_TEAMS_ILLUSTRATION_PATH)}
      alt=""
      aria-hidden="true"
      className={classes.illustration}
    />
  );
}

export interface ReadableTeamOption {
  id: string;
  name: string;
  organization: {
    id: string;
    name: string;
    urlIdentifier: string;
  };
}

export interface Principal {
  id: string;
  name: string;
  organizationRoles: Array<{
    organization: {
      id: string;
      name: string;
      urlIdentifier: string;
    };
  }>;
  teamRoles: Array<{
    role: TeamRole;
    team: {
      id: string;
      name: string;
      organization: {
        id: string;
        urlIdentifier: string;
      };
    };
  }>;
}

export const READABLE_TEAMS_QUERY = gql`
  query ReadableTeamsQuery($limitOffsetInput: LimitOffsetInput!) @engComponent(name: LLM) @skipAudit {
    currentTenantTeamListing(
      searchInput: {query: ""}
      sortInput: {sortKey: NAME, sortDirection: ASCENDING}
      limitOffsetInput: $limitOffsetInput
    ) {
      teams {
        id
        name
        organization {
          id
          name
          urlIdentifier
        }
      }
    }
  }
`;

// No tenant has 500+ teams; revisit if that changes or we add team search.
const READABLE_TEAMS_LIMIT = 500;

function formatPrincipalTypeLabel(typeName: string): string {
  return typeName === 'agent' ? 'Agent' : typeName;
}

export function getPrincipalTypeName(principalId: string): string {
  return formatPrincipalTypeLabel(typeToName(APIUtil.apiIdToType(principalId)));
}

function principalTypeLabel(principalType: ModelTypeConstants): string {
  return formatPrincipalTypeLabel(typeToName(principalType));
}

export function formatTeamRoleLabel(role: TeamRole): string {
  return role === TeamRole.ADMIN ? 'Admin' : 'Member';
}

const useTeamMembershipCardStyles = createStyles((theme) => ({
  card: {
    overflow: 'hidden',
  },
  previewCard: {
    overflow: 'hidden',
    '&[data-with-border]': {
      borderStyle: 'dashed',
      borderWidth: '2px',
    },
  },
  cardBody: {
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
  },
  cardFooter: {
    borderTop: `1px solid ${theme.colors.gray[3]}`,
    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
    backgroundColor: theme.colors.gray[0],
  },
}));

const useTeamMembershipAttentionIconStyles = createStyles((theme) => ({
  icon: {
    color: theme.colors.yellow[7],
  },
}));

export type TeamMembershipCardVariant = 'added' | 'preview';

export interface TeamMembershipCardProps {
  teamId: string;
  teamName: string;
  role: TeamRole;
  variant: TeamMembershipCardVariant;
  principalId: string;
  principalName: string;
  organizationUrlIdentifier: string;
  isInOrganization: boolean;
  onRemove?: () => void;
  testComponent?: string;
}

function TeamMembershipStatusBadge({variant}: {variant: TeamMembershipCardVariant}): JSX.Element {
  const isPreview = variant === 'preview';
  return (
    <Badge color={isPreview ? 'orange' : 'blue'} variant="light">
      <Text size="xs" p="xs" tt="none">
        {isPreview ? 'Preview' : 'Added'}
      </Text>
    </Badge>
  );
}

function TeamMembershipAttentionFooter({
  principalId,
  principalName,
  organizationUrlIdentifier,
  teamId,
  teamName,
  isInOrganization,
  onRemove,
}: {
  principalId: string;
  principalName: string;
  organizationUrlIdentifier: string;
  teamId: string;
  teamName: string;
  isInOrganization: boolean;
  onRemove: () => void;
}): JSX.Element {
  const {classes} = useTeamMembershipAttentionIconStyles();
  const organizationMembershipLabel = `organization ${getPrincipalTypeName(principalId)}`;

  return (
    <Group align="center" spacing="sm" noWrap={true}>
      <BenchlingIcon icon="warning" className={classes.icon} />
      <Text size="sm" color="dimmed">
        In-app membership requests are not yet supported. For now, ask{' '}
        {!isInOrganization ? 'an organization' : 'a team'} admin to add &ldquo;{principalName}&rdquo; as{' '}
        {!isInOrganization && (
          <>
            an{' '}
            <Anchor href={`/organizations/${organizationUrlIdentifier}/agents`} target="_blank" size="sm">
              {organizationMembershipLabel}
            </Anchor>{' '}
            and then{' '}
          </>
        )}
        a{' '}
        <Anchor href={`/teams/${teamId}/agents`} target="_blank" size="sm">
          team member.
        </Anchor>
      </Text>
      <Button
        variant={'subtle' /* "ghost" does not work as it adds a white button atop our grey background */}
        color="red"
        onClick={onRemove}
        aria-label={`Remove ${teamName}`}
      >
        Remove
      </Button>
    </Group>
  );
}

export function TeamMembershipCard({
  teamId,
  teamName,
  role,
  variant,
  principalId,
  principalName,
  organizationUrlIdentifier,
  isInOrganization,
  onRemove,
  testComponent = 'RoutineDataAccess',
}: TeamMembershipCardProps): JSX.Element {
  const {classes} = useTeamMembershipCardStyles();
  const isPreview = variant === 'preview';
  const principalTypeName = getPrincipalTypeName(principalId);

  return (
    <Paper
      className={isPreview ? classes.previewCard : classes.card}
      component="section"
      aria-label={`${teamName} team access for ${principalTypeName}`}
      withBorder={true}
      {...getTestProps({
        component: testComponent,
        element: isPreview ? 'pending-team' : 'team',
        key: teamId,
      })}
      data-amp-safe={false}
    >
      <Box className={classes.cardBody}>
        <Group position="apart" align="flex-start" noWrap={true}>
          <Stack spacing="xs">
            <Text size="lg">{teamName}</Text>
            <Text color="dimmed">{formatTeamRoleLabel(role)}</Text>
          </Stack>
          <TeamMembershipStatusBadge variant={variant} />
        </Group>
      </Box>
      {isPreview && onRemove != null ? (
        <Box className={classes.cardFooter}>
          <TeamMembershipAttentionFooter
            principalId={principalId}
            teamId={teamId}
            teamName={teamName}
            principalName={principalName}
            organizationUrlIdentifier={organizationUrlIdentifier}
            isInOrganization={isInOrganization}
            onRemove={onRemove}
          />
        </Box>
      ) : null}
    </Paper>
  );
}

export function TeamMembershipHeader({
  principalType,
  addTeamControl,
}: {
  principalType: ModelTypeConstants;
  addTeamControl?: JSX.Element | null;
}): JSX.Element {
  return (
    <Stack spacing="xs">
      <Group position="apart" align="flex-start" noWrap={true}>
        <Title order={4}>Team Access</Title>
        {addTeamControl}
      </Group>
      <Text size="sm" color="dimmed">
        Teams this {principalTypeLabel(principalType)} belongs to determine which data it can access when
        running as its own identity.
      </Text>
    </Stack>
  );
}

export function TeamMembershipCreateGuidance({
  principalType,
  testComponent = 'RoutineDataAccess',
}: {
  principalType: ModelTypeConstants;
  testComponent?: string;
}): JSX.Element {
  const principalTypeName = principalTypeLabel(principalType);
  return (
    <Stack spacing="md" {...getTestProps({component: testComponent, element: 'team-access'})}>
      <TeamMembershipHeader principalType={principalType} />
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text="Joining a team"
        supportingInfo={
          <Stack spacing="xs">
            <Text size="sm">After finishing Agent setup, you'll need to:</Text>
            <List type="ordered" size="sm">
              <List.Item>
                ask an organization admin to add your {principalTypeName} to your organization
              </List.Item>
              <List.Item>
                ask the relevant team admins to add your {principalTypeName} to your teams
              </List.Item>
            </List>
          </Stack>
        }
      />
    </Stack>
  );
}

export function TeamMembershipCardList({
  principal,
  canEdit,
  testComponent = 'RoutineDataAccess',
}: {
  principal: Principal;
  canEdit: boolean;
  testComponent?: string;
}): JSX.Element {
  const [pendingTeams, setPendingTeams] = useState<Array<ReadableTeamOption>>([]);
  const teamRoles = principal.teamRoles;
  const principalType = APIUtil.apiIdToType(principal.id);
  const principalOrganizationIds = useMemo(
    () => new Set(principal.organizationRoles.map((organizationRole) => organizationRole.organization.id)),
    [principal.organizationRoles]
  );
  const excludedTeamIds = useMemo(() => {
    const ids = new Set(teamRoles.map((teamRole) => teamRole.team.id));
    pendingTeams.forEach((team) => ids.add(team.id));
    return ids;
  }, [pendingTeams, teamRoles]);

  const handleSelectTeam = useCallback((team: ReadableTeamOption) => {
    setPendingTeams((current) => [team, ...current.filter((candidate) => candidate.id !== team.id)]);
  }, []);

  const handleRemoveTeam = useCallback((teamId: string) => {
    setPendingTeams((current) => current.filter((candidate) => candidate.id !== teamId));
  }, []);

  const teamsListingResult = useBenchlingQuery<ReadableTeamsQuery, ReadableTeamsQueryVariables>(
    READABLE_TEAMS_QUERY,
    {variables: {limitOffsetInput: {limit: READABLE_TEAMS_LIMIT, offset: 0}}}
  );
  const tenantTeams =
    teamsListingResult.type === QueryResultType.SUCCESS
      ? teamsListingResult.data.currentTenantTeamListing.teams
      : null;
  const tenantHasTeams = tenantTeams != null && tenantTeams.length > 0;
  const showAddTeamControl = tenantTeams == null || tenantTeams.length > 0;

  const addTeamControl = canEdit ? (
    <Box
      sx={{
        flexShrink: 0,
        visibility: showAddTeamControl ? 'visible' : 'hidden',
        pointerEvents: showAddTeamControl ? 'auto' : 'none',
      }}
    >
      <TeamMembershipAddTeamDropdown excludedTeamIds={excludedTeamIds} onSelectTeam={handleSelectTeam} />
    </Box>
  ) : null;

  const hasMembershipCards = pendingTeams.length > 0 || teamRoles.length > 0;
  return (
    <Stack spacing="md" {...getTestProps({component: testComponent, element: 'team-access'})}>
      <TeamMembershipHeader addTeamControl={addTeamControl} principalType={principalType} />
      {canEdit &&
        teamsListingResult.type === QueryResultType.SUCCESS &&
        !hasMembershipCards &&
        !tenantHasTeams && (
          <InlineBanner
            bannerType={InlineBannerType.INFO}
            text="No teams are accessible."
            supportingInfo={`Ask an organization admin to add your ${principalTypeLabel(principalType)} as a collaborator to specific projects.`}
          />
        )}
      {canEdit && !hasMembershipCards && tenantHasTeams ? (
        <Stack align="center" spacing="xs" pt="60px" pb="xl">
          <EmptyTeamsIllustration />
          <Title order={5}>No teams added yet</Title>
          <Text size="sm" color="dimmed">
            Add one to get started.
          </Text>
          <Text size="xs" color="dimmed">
            Don't see the right team? Ask an admin to create one.
          </Text>
        </Stack>
      ) : hasMembershipCards ? (
        <Stack spacing="md">
          {pendingTeams.map((team) => (
            <TeamMembershipCard
              key={team.id}
              isInOrganization={principalOrganizationIds.has(team.organization.id)}
              organizationUrlIdentifier={team.organization.urlIdentifier}
              principalId={principal.id}
              principalName={principal.name}
              role={TeamRole.MEMBER}
              teamId={team.id}
              teamName={team.name}
              variant="preview"
              testComponent={testComponent}
              onRemove={() => {
                handleRemoveTeam(team.id);
              }}
            />
          ))}
          {teamRoles.map((teamRole) => (
            <TeamMembershipCard
              key={teamRole.team.id}
              isInOrganization={principalOrganizationIds.has(teamRole.team.organization.id)}
              organizationUrlIdentifier={teamRole.team.organization.urlIdentifier}
              principalId={principal.id}
              principalName={principal.name}
              role={teamRole.role}
              teamId={teamRole.team.id}
              teamName={teamRole.team.name}
              variant="added"
              testComponent={testComponent}
            />
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
}

export function TeamMembershipEditPanel({
  principal,
  principalId,
  isLoading,
  hasError,
  canEdit,
  testComponent = 'RoutineDataAccess',
}: {
  // Principal may be null while loading.
  principal: Principal | null;
  principalId: string;
  isLoading: boolean;
  hasError: boolean;
  canEdit: boolean;
  testComponent?: string;
}): JSX.Element {
  const principalType = APIUtil.apiIdToType(principalId);
  if (hasError) {
    return (
      <Stack spacing="md" {...getTestProps({component: testComponent, element: 'team-access'})}>
        <TeamMembershipHeader principalType={principalType} />
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text="Could not load team access"
          supportingInfo="Try reloading the page."
        />
      </Stack>
    );
  }
  if (isLoading || principal == null) {
    return (
      <Stack spacing="md" {...getTestProps({component: testComponent, element: 'team-access'})}>
        <TeamMembershipHeader principalType={principalType} />
        <CenteredSpinner aria-label="Loading team access" />
      </Stack>
    );
  }

  return <TeamMembershipCardList canEdit={canEdit} principal={principal} testComponent={testComponent} />;
}

interface TeamMembershipAddTeamDropdownProps {
  excludedTeamIds: Set<string>;
  onSelectTeam: (team: ReadableTeamOption) => void;
}

export function TeamMembershipAddTeamDropdown({
  excludedTeamIds,
  onSelectTeam,
}: TeamMembershipAddTeamDropdownProps): JSX.Element {
  const teamsResult = useBenchlingQuery<ReadableTeamsQuery, ReadableTeamsQueryVariables>(
    READABLE_TEAMS_QUERY,
    {variables: {limitOffsetInput: {limit: READABLE_TEAMS_LIMIT, offset: 0}}}
  );

  const optionDatas = useMemo((): Array<OptionData> => {
    switch (teamsResult.type) {
      case QueryResultType.PENDING: {
        return [{name: 'Loading teams...', value: 'loading', disabled: true}];
      }
      case QueryResultType.ERROR: {
        // No retry UI; refresh is the usual recovery (global GraphQL error handler).
        return [{name: 'Could not load teams', value: 'teams-error', disabled: true}];
      }
      case QueryResultType.SUCCESS: {
        const teams = teamsResult.data.currentTenantTeamListing.teams.filter(
          (team) => !excludedTeamIds.has(team.id)
        );
        return sortedBy(teams, [(team) => team.name.toLowerCase()]).map((team) => ({
          name: team.name,
          value: team.id,
          icon: 'team',
        }));
      }
      default: {
        assertNever(teamsResult);
        return [];
      }
    }
  }, [excludedTeamIds, teamsResult]);

  const menu = (
    <SearchableMenu
      onSelect={(teamId: string) => {
        if (teamId === 'loading' || teamId === 'teams-error') {
          return;
        }
        assert(teamsResult.type === QueryResultType.SUCCESS, 'Expected teams to be loaded');
        const team = teamsResult.data.currentTenantTeamListing.teams.find(
          (candidate) => candidate.id === teamId
        );
        assert(team != null, 'Expected selected team to be loaded');
        onSelectTeam(team);
      }}
      optionDatas={optionDatas}
      height={MenuHeight.MEDIUM}
      width={MenuWidth.LARGE}
      dropLeft={true}
    />
  );

  return (
    <Dropdown closeOnInsideClick={true} closeOnOutsideClick={true} menu={menu}>
      <Button
        variant="ghost"
        leftIcon={<BenchlingIcon icon="plus" />}
        size="sm"
        {...getTestProps({component: 'RoutineDataAccess', element: 'add-team'})}
      >
        Add team
      </Button>
    </Dropdown>
  );
}
